/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */
import { decodeHTML } from 'entities';

import mbpLogger from '@module-logger/mbp';

import * as dataLayerProductHelpers from '../../product/dataLayerProductHelpers';
import { dateStringReformat } from '../../../../components/GraphqlComponents/GraphqlProductPage/Partials/common/commonProductPageHelper';
import { brandByCode } from '../../../../components/Checkout/containers/CheckoutFood/components/Partials/Helper/Helper';

import { getActiveBannerProps, setActiveBannerProps } from '../../banner/activeBannerHelpers';

export const getDomainBrandName = (host) => {
    if (!host) return '';

    const allBrands = ['1800flowers', '1800baskets', 'berries', 'cheryls', 'fruitbouquets', 'harryanddavid', 'personalizationmall', 'plants', 'simplychocolate', 'thepopcornfactory', 'vitalchoice', 'wolfermans', 'celebrations', 'cardisle'];
    const lowerHost = host.toLowerCase();
    return allBrands.find((brand) => lowerHost.includes(brand)) || '';
};

export { getActiveBannerProps, setActiveBannerProps };

export const getFacetsFromUrlSearch = (searchStr) => {
    if (!searchStr) return [];
    try {
        const decoded = decodeURIComponent(searchStr);
        let rawQuery = decoded;
        if (rawQuery.startsWith('?')) {
            rawQuery = rawQuery.substring(1);
        }

        const facets = [];
        const params = rawQuery.split('&');

        params.forEach((param) => {
            if (!param) return;
            let cleanParam = param;
            if (cleanParam.startsWith('facet=')) {
                cleanParam = cleanParam.substring(6);
            } else if (cleanParam.startsWith('facets=')) {
                cleanParam = cleanParam.substring(7);
            }

            if (!cleanParam) return;

            if (cleanParam.includes('=')) {
                const [k] = cleanParam.split('=');
                if (['searchterm', 'search_term', 'sort', 'sortby', 'page', 'pagesize', 'qs', 'q'].includes(k.toLowerCase())) {
                    return;
                }
            }

            facets.push(cleanParam);
        });

        return facets.filter(Boolean);
    } catch (e) {
        return [];
    }
};

export const formatFiltersSpec = (rawFacets) => {
    if (!rawFacets) return '';

    let facetList = [];
    if (Array.isArray(rawFacets)) {
        facetList = rawFacets;
    } else if (typeof rawFacets === 'string') {
        if (!rawFacets.trim()) return '';
        if (rawFacets.includes('|')) {
            facetList = rawFacets.split('|');
        } else if (rawFacets.includes('&')) {
            facetList = rawFacets.split('&');
        } else {
            facetList = [rawFacets];
        }
    } else {
        return '';
    }

    const filterMap = new Map();

    facetList.forEach((item) => {
        if (!item) return;

        let name = '';
        let value = '';

        if (typeof item === 'object') {
            name = item.facetName || item.name || item.filterName || item.groupName || item.facetId || 'facet';
            value = item.facetValue || item.value || item.val || item.entryValue || '';
        } else if (typeof item === 'string') {
            let str = item.trim();
            if (!str) return;

            if (str.startsWith('facet=')) {
                str = str.substring(6).trim();
            } else if (str.startsWith('facets=')) {
                str = str.substring(7).trim();
            }

            if (!str) return;

            if (str.includes('=')) {
                const parts = str.split('=');
                name = parts[0].trim();
                value = parts.slice(1).join('=').trim();
            } else if (str.includes('|')) {
                const parts = str.split('|');
                name = parts[0].trim();
                value = parts.slice(1).join('|').trim();
            } else {
                name = 'facet';
                value = str;
            }
        }

        if (!name) {
            name = 'facet';
        }

        if (value) {
            const valParts = String(value).split(/[&,]/);
            if (!filterMap.has(name)) {
                filterMap.set(name, new Set());
            }
            valParts.forEach((v) => {
                const cleanV = v.trim();
                if (cleanV) {
                    filterMap.get(name).add(cleanV);
                }
            });
        }
    });

    if (filterMap.size === 0) return '';

    // Alphabetically sort filter keys so output is 100% consistent across all events (view_item_list, select_item, view_item, add_to_cart, view_cart, purchase)
    const keys = Array.from(filterMap.keys()).sort();

    const filterSegments = [];
    keys.forEach((filterName) => {
        const valueSet = filterMap.get(filterName);
        if (valueSet && valueSet.size > 0) {
            const valuesJoined = Array.from(valueSet).join('&');
            filterSegments.push(`${filterName}=${valuesJoined}`);
        }
    });

    return filterSegments.join('|');
};

export const getSelectedFiltersFromWindowOrUrl = () => {
    if (typeof window === 'undefined') return '';

    try {
        if (window.selectedFacets && Array.isArray(window.selectedFacets) && window.selectedFacets.length > 0) {
            return formatFiltersSpec(window.selectedFacets);
        }

        if (window.location && window.location.search) {
            const facetsFromUrl = getFacetsFromUrlSearch(window.location.search);
            if (facetsFromUrl && facetsFromUrl.length > 0) {
                return formatFiltersSpec(facetsFromUrl);
            }
        }
    } catch (e) {
        // ignore
    }

    return '';
};

export const getSearchTermFromWindowOrUrl = () => {
    if (typeof window === 'undefined') return '';

    try {
        if (window.rawDataLayer?.search_phrase) {
            return window.rawDataLayer.search_phrase;
        }
        if (window.rawDataLayer?.searchTerm || window.rawDataLayer?.search_term) {
            return window.rawDataLayer.searchTerm || window.rawDataLayer.search_term;
        }
        if (window.mbpDataLayer) {
            const searchLayer = window.mbpDataLayer.find((layer) => layer?.search_phrase || layer?.search_term || layer?.searchTerm);
            if (searchLayer) {
                return searchLayer.search_phrase || searchLayer.search_term || searchLayer.searchTerm;
            }
        }

        const pathname = window.location?.pathname || '';
        if (pathname.includes('/searchterm/')) {
            const rawTerm = pathname.split('/searchterm/')[1]?.split('?')[0]?.split('/')[0];
            if (rawTerm) {
                return decodeURIComponent(rawTerm).replace(/\+/g, ' ');
            }
        }

        if (window.location && window.location.search) {
            const searchParams = new URLSearchParams(window.location.search);
            const termParam = searchParams.get('searchTerm') || searchParams.get('search_term') || searchParams.get('searchterm') || searchParams.get('q');
            if (termParam) {
                return decodeURIComponent(termParam).replace(/\+/g, ' ');
            }
        }
    } catch (e) {
        // ignore
    }

    return '';
};

export const getActiveSortFromWindowOrUrl = () => {
    if (typeof window === 'undefined') return '';

    try {
        if (window.location && window.location.search) {
            const searchParams = new URLSearchParams(window.location.search);
            const sortParam = searchParams.get('sort') || searchParams.get('sortBy') || searchParams.get('sortby');
            if (sortParam) {
                return sortParam;
            }
            return '';
        }
        if (window.sortBy) {
            return window.sortBy;
        }
    } catch (e) {
        // ignore
    }

    return '';
};

export const saveSortFiltersToLocalStorage = (data = {}) => {
    if (typeof window === 'undefined' || !data) return;
    const pid = data.partNumber || data.variant || data.productCode || data.id;
    const variant = data.variant || data.productCode || data.partNumber || data.id;
    if (!pid && !variant) return;

    try {
        let sortFilters = [];
        if (localStorage.getItem('sortFilters')) {
            try {
                sortFilters = JSON.parse(localStorage.getItem('sortFilters')) || [];
            } catch (e) {
                sortFilters = [];
            }
        }
        if (!Array.isArray(sortFilters)) {
            sortFilters = [];
        }

        const fallbackSearchTerm = getSearchTermFromWindowOrUrl();
        const fallbackFilters = getSelectedFiltersFromWindowOrUrl();
        const fallbackSort = getActiveSortFromWindowOrUrl();

        const activeBannerProps = getActiveBannerProps();

        const existingIdx = sortFilters.findIndex(
            (item) => item?.variant === pid || item?.partNumber === pid || item?.variant === variant || item?.partNumber === variant
                || (item?.variant && pid && (item.variant.includes(pid) || pid.includes(item.variant)))
                || (item?.partNumber && pid && (item.partNumber.includes(pid) || pid.includes(item.partNumber))),
        );

        const existingEntry = existingIdx >= 0 ? sortFilters[existingIdx] : {};

        const rawDataTerm = (typeof window !== 'undefined' && window.rawDataLayer?.search_phrase) || (typeof window !== 'undefined' && window.rawDataLayer?.searchTerm) || '';

        const sanitize = (val) => (val && val !== 'undefined' ? val : undefined);

        const resolvedSearchTerm = sanitize(data.search_term || data.searchTerm || fallbackSearchTerm || rawDataTerm || existingEntry.search_term);
        const resolvedFilters = sanitize(formatFiltersSpec(data.filters || data.selectedFilters || fallbackFilters || existingEntry.filters));
        const resolvedSort = sanitize(data.sort || data.selectedSort || fallbackSort);
        const resolvedIndex = data.index || data.itemListPosition || existingEntry.index || undefined;
        const resolvedBannerType = sanitize(data.banner_type || data.bannerType || activeBannerProps?.bannerType || existingEntry.banner_type);
        const resolvedBannerGroup = sanitize(data.banner_group || data.bannerGroup || activeBannerProps?.bannerGroup || existingEntry.banner_group);
        const resolvedBannerItem = sanitize(data.banner_item_name || data.bannerItemName || data.bannerItem || activeBannerProps?.bannerItemName || existingEntry.banner_item_name);
        const resolvedItemCategory = sanitize(data.item_category || data.itemCategory || (typeof window !== 'undefined' ? window?.real_product_category?.[0] : undefined) || existingEntry.item_category);
        const resolvedIsCartItem = data.isCartItem !== undefined ? data.isCartItem : (existingEntry.isCartItem || false);

        const resolvedVariantType = sanitize(
            data.variant_type
            || data.item_variant_type
            || (Array.isArray(data.product_variant_type) ? data.product_variant_type[0] : data.product_variant_type)
            || existingEntry.variant_type
            || 'default',
        );

        const updatedEntry = {
            partNumber: pid,
            variant,
            sort: resolvedSort,
            filters: resolvedFilters,
            index: resolvedIndex,
            search_term: resolvedSearchTerm,
            banner_type: resolvedBannerType,
            banner_group: resolvedBannerGroup,
            banner_item_name: resolvedBannerItem,
            item_category: resolvedItemCategory,
            isCartItem: resolvedIsCartItem,
            variant_type: resolvedVariantType,
        };

        if (existingIdx >= 0) {
            sortFilters[existingIdx] = updatedEntry;
        } else {
            sortFilters.push(updatedEntry);
        }

        localStorage.setItem('sortFilters', JSON.stringify(sortFilters));
    } catch (e) {
        // ignore
    }
};

export const valueFromLocalStorage = (pid) => {
    if (typeof window !== 'undefined') {
        let sortFilters = [];
        if (localStorage.getItem('sortFilters')) {
            try {
                sortFilters = JSON.parse(localStorage.getItem('sortFilters'));
            } catch (e) {
                sortFilters = [];
            }
        }
        if (sortFilters?.length > 0) {
            let found = null;
            if (pid) {
                const cleanPid = String(pid).trim();
                found = sortFilters.find(
                    (item) => item?.variant === cleanPid || item?.partNumber === cleanPid
                        || (item?.variant && cleanPid && (item.variant.includes(cleanPid) || cleanPid.includes(item.variant)))
                        || (item?.partNumber && cleanPid && (item.partNumber.includes(cleanPid) || cleanPid.includes(item.partNumber))),
                );
            }
            if (!found) {
                found = sortFilters[sortFilters.length - 1];
            }

            return {
                ...found,
                filters: found?.filters || undefined,
                sort: found?.sort || undefined,
                search_term: found?.search_term || undefined,
                variant_type: found?.variant_type || undefined,
            };
        }
    }
    return {};
};

export const removeSortFiltersFromLocalStorage = (pidOrEvent) => {
    if (typeof window === 'undefined' || !pidOrEvent) return;

    try {
        let pids = [];
        if (typeof pidOrEvent === 'string' || typeof pidOrEvent === 'number') {
            pids.push(String(pidOrEvent).trim());
        } else if (typeof pidOrEvent === 'object') {
            const rawItems = pidOrEvent?.ecommerce?.items || pidOrEvent?.products || (Array.isArray(pidOrEvent) ? pidOrEvent : [pidOrEvent]);
            if (Array.isArray(rawItems)) {
                rawItems.forEach((item) => {
                    if (item?.item_id) pids.push(String(item.item_id).trim());
                    if (item?.partNumber) pids.push(String(item.partNumber).trim());
                    if (item?.variant) pids.push(String(item.variant).trim());
                    if (item?.item_variant) pids.push(String(item.item_variant).trim());
                    if (item?.id) pids.push(String(item.id).trim());
                });
            }
            if (pidOrEvent?.eventLabel && typeof pidOrEvent.eventLabel === 'string') {
                const labelPid = pidOrEvent.eventLabel.split('|')?.[0];
                if (labelPid) pids.push(labelPid.trim());
            }
        }

        pids = pids.filter(Boolean);
        if (pids.length === 0) return;

        if (localStorage.getItem('sortFilters')) {
            const sortFilters = JSON.parse(localStorage.getItem('sortFilters')) || [];
            if (Array.isArray(sortFilters) && sortFilters.length > 0) {
                const filtered = sortFilters.filter((item) => {
                    const itemPartNumber = String(item?.partNumber || '');
                    const itemVariant = String(item?.variant || '');

                    return !pids.some((pid) => (
                        itemVariant === pid || itemPartNumber === pid
                        || (itemVariant && pid && (itemVariant.includes(pid) || pid.includes(itemVariant)))
                        || (itemPartNumber && pid && (itemPartNumber.includes(pid) || pid.includes(itemPartNumber)))
                    ));
                });
                localStorage.setItem('sortFilters', JSON.stringify(filtered));
            }
        }
    } catch (e) {
        // ignore
    }
};

export const cleanUncartedSortFilters = () => {
    if (typeof window === 'undefined') return;

    try {
        if (localStorage.getItem('sortFilters')) {
            const sortFilters = JSON.parse(localStorage.getItem('sortFilters')) || [];
            if (Array.isArray(sortFilters) && sortFilters.length > 0) {
                const currentPath = window.location?.pathname || '';
                const filtered = sortFilters.filter((item) => {
                    if (item?.isCartItem) return true;
                    const pid = item?.partNumber || item?.variant;
                    if (pid && currentPath.includes(`-${pid}`)) return true;
                    return false;
                });
                localStorage.setItem('sortFilters', JSON.stringify(filtered));
            }
        }
    } catch (e) {
        // ignore
    }
};

// Build Product arrays
export const generateDataLayerProductArrays = ({
    inStock = false,
    products,
    impressions = false,
    props = {},
    rawDataLayer = {},
}) => {
    const MAX_PRODUCT_COUNT = 60; // ? for GTM
    const tempDataLayer = {};

    // Products
    const productIds = [];
    const productDiscount = [];
    const productItemShipping = [];
    const productTypeState = [];
    const productNames = [];
    const productPrices = [];
    const totalProductPrice = [];
    const productSkus = [];
    const productBaseCodes = [];
    const productBrands = [];
    const productCategoryIds = [];
    const productCategoryLegacyIds = [];
    const productCategoryNames = [];
    const productCategoryPaths = [];
    const productVariants = [];
    const productListNames = [];
    const productListPositions = [];
    const productQuantities = [];
    const productIsShipInFuture = [];
    const productShipDate = [];
    const productBrandsName = [];

    const productCurrency = [];
    const productHasWine = [];
    const productImageUrls = [];
    const productInStock = [];
    const productDeliveryTypes = [];
    const productDeliveryDates = [];
    const productDeliveryLocation = [];
    // Impressions
    const productImpressionBrands = [];
    const productImpressionBrandsName = [];
    const productImpressionCategoryIds = [];
    const productImpressionCategoryLegacyIds = [];
    const productImpressionCategoryNames = [];
    const productImpressionCategoryPaths = [];
    const productImpressionIds = [];
    const productImpressionBaseCodes = [];
    const productImpressionListNames = [];
    const productImpressionListPositions = [];
    const productImpressionNames = [];
    const productImpressionSkus = [];
    const productImpressionPrices = [];
    const productImpressionVariants = [];
    const productImpressionShipDate = [];
    const productImpressionShipInFuture = [];

    const productImpressionCurrency = [];
    const productImpressionHasWine = [];

    // subscriptions
    const productSubscriptionDuration = [];
    const productSubscriptionType = [];
    const productSubscriptionInterval = [];
    const productBrandExpanded = [];
    const productItemType = [];
    const productAffiliation = [];

    const productIndex = [];
    const productGoogleBusinessVertical = [];
    const productTypeInfo = [];
    const productSearchTerm = [];

    const productRealCategoryName = [];
    const productRealCategoryName2 = [];
    const productRealCategoryName3 = [];
    const productRealCategoryName4 = [];
    const productRealCategoryName5 = [];

    const bannerType = [];
    const selectedSort = [];
    const selectedFilters = [];
    const itemListPosition = [];
    const searchTerm = [];
    const bannerGroup = [];
    const itemCategory = [];
    const bannerItem = [];

    const tld = typeof window !== 'undefined' && window.location && (window.location.hostname?.split?.('.').pop() || '');
    try {
        const { category } = props;
        Object.keys(products).forEach((key, idx) => {
            try {
                // Limit Product list to MAX_PRODUCT_COUNT
                if (idx < MAX_PRODUCT_COUNT) {
                    const product = products[key];

                    const skus = (product.sKUs) ? product.sKUs.map((sku) => sku.partNumber) : [product.selectedSku || product.partNumber];
                    const addons = (product?.addOns || []).map((item) => item);

                    // Valid product
                    if (dataLayerProductHelpers.isValidProduct(product)) {
                        const pid = product?.sKUs?.[0]?.partNumber || product?.partNumber || product?.productCode || product?.variant || product?.id || product?.sku;
                        const persistedData = valueFromLocalStorage(pid);

                        const activeBannerProps = getActiveBannerProps();

                        const bannerTypeValue = activeBannerProps?.bannerType;
                        const bannerGroupValue = activeBannerProps?.bannerGroup;
                        const bannerItemName = activeBannerProps?.bannerItemName;

                        const fallbackSearchTerm = getSearchTermFromWindowOrUrl();
                        const fallbackFilters = getSelectedFiltersFromWindowOrUrl();

                        const rawBannerType = persistedData?.banner_type || product.bannerType || bannerTypeValue || '';
                        const rawBannerGroup = persistedData?.banner_group || product.bannerGroup || bannerGroupValue || '';
                        const rawBannerItem = persistedData?.banner_item_name || product.bannerItem || bannerItemName || '';

                        const isCartOrCheckoutPage = (typeof window !== 'undefined' && (
                            window.location?.pathname?.includes('/cart')
                            || window.location?.pathname?.includes('/checkout')
                            || window.location?.pathname?.includes('/review-order')
                        )) || ['shoppingCart', 'checkout', 'placeorder'].includes(props?.pageType);

                        const isPdpPage = ['product', 'pdp', 'item'].includes(props?.pageType)
                            || (typeof window !== 'undefined' && (
                                window.location?.pathname?.includes('/gifts-')
                                || (product?.partNumber && window.location?.pathname?.includes(`-${product.partNumber}`))
                            ));

                        const isAddToCartEvent = (rawDataLayer?.eventName === 'add_to_cart' || rawDataLayer?.eventAction === 'Add to Cart' || rawDataLayer?.ga_event_name === 'add_to_cart' || rawDataLayer?.event === 'add_to_cart');

                        const isPersistedContextPage = isCartOrCheckoutPage || isPdpPage || isAddToCartEvent;

                        const rawSearchTerm = isPersistedContextPage
                            ? (persistedData?.search_term || product.searchTerm || props.searchTerm || fallbackSearchTerm || '')
                            : (product.searchTerm || props.searchTerm || fallbackSearchTerm || '');

                        const fallbackItemCategory = (isPersistedContextPage ? persistedData?.item_category : undefined) || product.itemCategory || product.categoryName || (category ? decodeHTML(category?.shortDescription || category?.name || '') : '');
                        const rawItemCategory = fallbackItemCategory || '';

                        const rawFilters = isPersistedContextPage
                            ? (formatFiltersSpec(product.filters || product.selectedFilters || props.selectedFilters || persistedData?.filters || fallbackFilters) || '')
                            : (formatFiltersSpec(product.filters || product.selectedFilters || props.selectedFilters || fallbackFilters) || '');

                        const activeUrlSort = getActiveSortFromWindowOrUrl() || product.selectedSort || props.selectedSort || '';
                        const rawSort = isPersistedContextPage
                            ? (product.sort || product.selectedSort || props.selectedSort || persistedData?.sort || activeUrlSort || '')
                            : (product.sort || product.selectedSort || props.selectedSort || activeUrlSort || '');

                        let resolvedPosition = product.itemListPosition || (idx + 1);
                        if (isCartOrCheckoutPage) {
                            resolvedPosition = idx + 1;
                        } else if (isPersistedContextPage) {
                            resolvedPosition = persistedData?.index || product.itemListPosition || (idx + 1);
                        }

                        // Allow for seeding product list position
                        const productCount = props.productListPositionSeed ? ((idx + props.productListPositionSeed) + 1) : (idx + 1);

                        const selectedSkuData = product?.productSkus?.find?.((item) => item?.id === product?.selectedSku) || null;

                        const isWineProduct = selectedSkuData?.ageVerifyFlag === 'true' || product?.ageVerifyFlag === 'true';
                        // Impressions (Category / Search / UpSells / AddOns / Components)
                        if (impressions) {
                            productImpressionIds.push(dataLayerProductHelpers.getBaseCode(product.partNumber || ''));
                            productImpressionNames.push(decodeHTML(product.name) || '');
                            productImpressionBaseCodes.push(product.baseCode || dataLayerProductHelpers.getBaseCode(product.partNumber) || '');
                            productImpressionPrices.push(dataLayerProductHelpers.getPrice(product) || '');
                            productImpressionSkus.push(...skus || '');
                            if (product?.variant) {
                                productImpressionVariants.push(product.variant || '');
                            } else {
                                productImpressionVariants.push(...skus || '');
                            }

                            productImpressionBrands.push(product.brandId || dataLayerProductHelpers.getBrandId(product.partNumber) || product.brand || '');
                            productImpressionBrandsName.push(product?.brand?.concat?.('.', tld) || '');
                            productImpressionListPositions.push(productCount || '');
                            productImpressionCurrency.push(product.currency || 'USD');
                            productImpressionHasWine.push(isWineProduct ? 'yes' : 'no');

                            if (category) { // Category-Based
                                productImpressionCategoryIds.push(category.id || '');
                                productImpressionCategoryLegacyIds.push(category.legacyId || category.id || '');
                                productImpressionCategoryNames.push(decodeHTML(category.shortDescription || category.name || ''));
                                productImpressionCategoryPaths.push(category.path || category.categoryPath || '');
                            } else { // Product-Based
                                productImpressionCategoryIds.push(product.categoryId || '');
                                productImpressionCategoryLegacyIds.push(product.categoryLegacyId || product.categoryId || '');
                                productImpressionCategoryNames.push((product.categoryName) ? decodeHTML(product.categoryName) : '');
                                productImpressionCategoryPaths.push(product.categoryPath || '');
                            }

                            let defaultCatName = '';
                            if (category) {
                                defaultCatName = decodeHTML(category.shortDescription || category.name || '');
                            }
                            const fallbackList = props.searchTerm ? `Search: ${props.searchTerm}` : defaultCatName;
                            const listNameValue = product.list || product.categoryName || fallbackList;
                            productImpressionListNames.push(listNameValue || '');

                            // DIM113 & 114
                            if (product?.availability?.earliestShipDate) {
                                const shipmentData = dateStringReformat(product?.availability?.earliestShipDate, true);
                                const isFuture = (new Date() < new Date(shipmentData?.year, shipmentData?.month, shipmentData?.day));
                                productImpressionShipDate.push(isFuture ? dateStringReformat(product?.availability?.earliestShipDate) : '');
                                productImpressionShipInFuture.push(isFuture ? 'Yes' : 'No');
                            } else {
                                productImpressionShipDate.push('');
                                productImpressionShipInFuture.push('');
                            }
                        } else {
                            bannerType.push((rawBannerType && rawBannerType !== 'undefined') ? rawBannerType : '');
                            selectedSort.push((rawSort && rawSort !== 'undefined') ? rawSort : '');
                            selectedFilters.push((rawFilters && rawFilters !== 'undefined') ? rawFilters : '');
                            itemListPosition.push(resolvedPosition);
                            searchTerm.push((rawSearchTerm && rawSearchTerm !== 'undefined') ? rawSearchTerm : '');
                            bannerGroup.push((rawBannerGroup && rawBannerGroup !== 'undefined') ? rawBannerGroup : '');
                            bannerItem.push((rawBannerItem && rawBannerItem !== 'undefined') ? rawBannerItem : '');
                            itemCategory.push((rawItemCategory && rawItemCategory !== 'undefined') ? rawItemCategory : '');

                            const getCollectedDiscount = (product.itemDiscounts?.promotions || []).map((item) => Math.abs(Number(item?.discountAmount)));

                            productIds.push(dataLayerProductHelpers.getBaseCode(product.partNumber) || '');
                            productNames.push(decodeHTML(product.name) || '');
                            productPrices.push(Number(dataLayerProductHelpers.getPrice(product) || '0'));
                            totalProductPrice.push(Number(dataLayerProductHelpers.getPrice(product) || '0') * Number(product.quantity || 1));
                            if (dataLayerProductHelpers.sumValues(getCollectedDiscount)) {
                                productDiscount.push(Number(parseFloat(dataLayerProductHelpers.sumValues(getCollectedDiscount) / Number(product.quantity || 1)).toFixed(2)));
                            } else {
                                productDiscount.push('');
                            }
                            productItemShipping.push(product.shippingAmount || 0);
                            productSkus.push(...skus || ''); // TODO: DATALAYER - Find skus
                            productImageUrls.push(product?.image?.path);
                            productInStock.push(inStock);

                            productRealCategoryName.push(product?.categories?.[0]);
                            productRealCategoryName2.push(product?.categories?.[1]);
                            productRealCategoryName3.push(product?.categories?.[2]);
                            productRealCategoryName4.push(product?.categories?.[3]);
                            productRealCategoryName5.push(product?.categories?.[4]);

                            const defaultSku = product?.productSkus?.find?.((item) => item?.isDefaultSku);
                            if (product?.variant || defaultSku?.id || product?.productSkus?.[0]?.id) {
                                productVariants.push(product.variant || defaultSku?.id || product?.productSkus?.[0]?.id || '');
                            } else {
                                productVariants.push(...skus || '');
                            }
                            productBaseCodes.push(product.baseCode || dataLayerProductHelpers.getBaseCode(product.partNumber) || '');
                            productBrands.push(product.brandId || dataLayerProductHelpers.getBrandId(product.partNumber) || product.brand || '');
                            productBrandsName.push(product?.brand?.concat?.('.', tld) || '');
                            productBrandExpanded.push(brandByCode[Number(product?.brand)] || brandByCode[Number(product?.brandId)] || product?.brand);

                            if (typeof window !== 'undefined') {
                                productAffiliation.push(getDomainBrandName(window?.location?.host));
                            }

                            productListPositions.push(productCount || '');
                            productQuantities.push(product.quantity || 1); // by default added 1

                            productItemType.push(product.productType || '');

                            productCurrency.push(product.currency || 'USD');
                            productHasWine.push(isWineProduct ? 'yes' : 'no');
                            let delType;
                            if (product?.availability) {
                                delType = product?.availability?.productDeliveryType;
                            } else if (product?.deliveryInfo?.deliverySLA) {
                                delType = product?.deliveryInfo?.deliverySLA === 'FPT' ? 'FPT' : 'GPT';
                            } else if (product?.productDeliveryType) {
                                delType = product?.productDeliveryType === 'FPT' ? 'FPT' : 'GPT';
                            }
                            productDeliveryTypes.push(delType || '');

                            productDeliveryDates.push(product?.deliveryDate || '');
                            productDeliveryLocation.push(product?.locationType || '');

                            if (addons?.length > 0) {
                                (addons || []).forEach((item) => {
                                    productIds.push(dataLayerProductHelpers.getBaseCode(item.baseCode) || '');
                                    productNames.push(decodeHTML(item.productName) || '');
                                    productPrices.push(dataLayerProductHelpers.getPrice({ price: item.price || '' }));
                                    productSkus.push(item.sku || '');
                                    productBaseCodes.push(dataLayerProductHelpers.getBaseCode(item.baseCode) || '');
                                    productBrands.push(item.brandCode);
                                    productBrandsName.push(item?.brand?.concat?.('.', tld) || '');
                                    productQuantities.push(item.quantity || 1); // by default added 1
                                    productCurrency.push(item.currency || 'USD');
                                });
                            }

                            if (category) { // Category-Based
                                productCategoryIds.push(category.id || '');
                                productCategoryLegacyIds.push(category.legacyId || category.id || '');
                                productCategoryNames.push(decodeHTML(category.shortDescription || category.name || ''));
                                productCategoryPaths.push(category.path || category.categoryPath || '');
                            } else { // Product-Based
                                productCategoryIds.push(product.categoryId || rawDataLayer.category_id || '');
                                productCategoryLegacyIds.push(product.categoryLegacyId || product.categoryId || '');
                                productCategoryNames.push(rawDataLayer.category_name || (product.categoryName) ? decodeHTML(product.categoryName) : '');
                                productCategoryPaths.push(product.categoryPath || '');
                            }

                            const fallbackCartListName = (props.searchTerm || rawSearchTerm) ? 'Search' : '';
                            const resolvedCartListName = rawDataLayer.category_name || product.categoryName || decodeHTML(category?.shortDescription || category?.name || '') || rawItemCategory || fallbackCartListName;
                            productListNames.push(resolvedCartListName || '');

                            productSearchTerm.push((rawSearchTerm && rawSearchTerm !== 'undefined') ? rawSearchTerm : (rawDataLayer?.search_phrase || ''));

                            // DIM113 & 114
                            if (product?.availability?.earliestShipDate) {
                                const shipmentData = dateStringReformat(product?.availability?.earliestShipDate, true);
                                const isFuture = (new Date() < new Date(shipmentData?.year, shipmentData?.month, shipmentData?.day));
                                productShipDate.push(isFuture ? dateStringReformat(product?.availability?.earliestShipDate) : '');
                                productIsShipInFuture.push(isFuture ? 'Yes' : 'No');
                            } else {
                                productShipDate.push('');
                                productIsShipInFuture.push('');
                            }
                            const selectedItem = product?.productSkus?.find?.((item) => item?.id === product?.selectedSku) || product?.productSkus?.find?.((item) => item?.isDefaultSku) || product?.productSkus?.[0] || {};

                            // Subscription feeding to DL
                            productSubscriptionType.push(product?.subscription?.type || selectedItem?.subscriptions?.type || '');
                            productSubscriptionDuration.push(product?.subscription?.duration || selectedItem?.subscriptions?.duration || '');
                            productSubscriptionInterval.push(product?.subscription?.interval || selectedItem?.subscriptions?.interval || '');

                            const currentTypeState = selectedItem?.subscriptions?.type ? 'subscription' : product.productType;
                            productTypeState.push(currentTypeState === 'REGULARPRODUCT' ? 'REGULAR_PRODUCT' : currentTypeState);
                            const rawDataVariantType = Array.isArray(rawDataLayer?.product_variant_type)
                                ? (rawDataLayer.product_variant_type[idx] || rawDataLayer.product_variant_type[0])
                                : rawDataLayer?.product_variant_type;

                            const productVariantType = (Array.isArray(product?.product_variant_type) ? product.product_variant_type[0] : product?.product_variant_type)
                                || product?.variant_type
                                || product?.item_variant_type;

                            const resolvedVariantType = rawDataVariantType
                                || productVariantType
                                || (isPersistedContextPage ? persistedData?.variant_type : undefined)
                                || 'default';

                            productTypeInfo.push(resolvedVariantType);
                        }
                    }
                }
            } catch (ex) {
                mbpLogger.logError({
                    products,
                    impressions,
                    props,
                    function: 'generateDataLayerProductArrays',
                    appName: process.env.npm_package_name,
                    module: 'mbp-page',
                    jsError: ex,
                    message: 'Failed to add product to product arrays.',
                });
            }
        });

        // Impressions (Displayed to the customer)
        if (productImpressionIds.length) {
            tempDataLayer.product_imp_ids = productImpressionIds || [''];
            tempDataLayer.product_imp_base_codes = productImpressionBaseCodes || [''];
            tempDataLayer.product_imp_brands = productImpressionBrands || [''];
            tempDataLayer.product_imp_brands_name = productImpressionBrandsName || [''];
            tempDataLayer.product_imp_category_ids = productImpressionCategoryIds || [''];
            tempDataLayer.product_imp_category_legacy_ids = productImpressionCategoryLegacyIds || [''];
            tempDataLayer.product_imp_category_names = productImpressionCategoryNames || [''];
            tempDataLayer.product_imp_category_paths = productImpressionCategoryPaths || [''];
            tempDataLayer.product_imp_categories = productImpressionCategoryPaths || [''];
            tempDataLayer.product_imp_list_names = productImpressionListNames || [''];
            tempDataLayer.product_imp_list_positions = productImpressionListPositions || [''];
            tempDataLayer.product_imp_names = productImpressionNames || [''];
            tempDataLayer.product_imp_prices = productImpressionPrices || [''];
            tempDataLayer.product_imp_skus = productImpressionSkus || [''];
            tempDataLayer.product_imp_sku_ids = productImpressionSkus || [''];
            tempDataLayer.product_imp_variants = productImpressionVariants || [''];
            tempDataLayer.product_imp_currency = productImpressionCurrency || [''];
            // Dim113 & 114
            tempDataLayer.product_imp_is_future_dated = productImpressionShipDate;
            tempDataLayer.product_imp_future_date = productImpressionShipInFuture;
            tempDataLayer.product_imp_has_wine = productImpressionHasWine || [''];
        } else {
            tempDataLayer.product_ids = productIds || [''];
            tempDataLayer.product_names = productNames || [''];
            tempDataLayer.product_prices = productPrices || [''];
            tempDataLayer.product_price = productPrices[0] || '';
            tempDataLayer.product_sku_ids = productSkus || [''];
            tempDataLayer.product_skus = productSkus || [''];
            tempDataLayer.product_variants = productVariants || [''];
            tempDataLayer.product_base_codes = productBaseCodes || [''];
            tempDataLayer.product_brands = productBrands || [''];
            tempDataLayer.product_brands_name = productBrandsName || [''];
            tempDataLayer.product_affiliation = productAffiliation || [''];
            tempDataLayer.product_index = productIndex;
            tempDataLayer.product_variant_type = productTypeInfo;
            tempDataLayer.google_business_vertical = productGoogleBusinessVertical;
            tempDataLayer.product_brand_name_expanded = productBrandExpanded;
            tempDataLayer.product_category_ids = productCategoryIds || [''];
            tempDataLayer.product_category_legacy_ids = productCategoryLegacyIds || [''];
            tempDataLayer.product_category_names = productCategoryNames || [''];
            tempDataLayer.product_category_identifiers = productCategoryNames || [''];
            tempDataLayer.product_category_paths = productCategoryPaths || [''];
            tempDataLayer.product_categories = productCategoryPaths || [''];
            tempDataLayer.product_quantities = productQuantities || [''];
            tempDataLayer.product_list_names = productListNames || [''];
            tempDataLayer.product_list_positions = productListPositions || [''];
            tempDataLayer.product_has_wine = productHasWine || [''];
            tempDataLayer.product_image_urls = productImageUrls || [''];
            tempDataLayer.in_stock = inStock || [''];

            tempDataLayer.product_list_currency = productCurrency || [''];
            tempDataLayer.product_delivery_types = productDeliveryTypes || [''];
            tempDataLayer.product_delivery_dates = productDeliveryDates || [''];
            tempDataLayer.product_item_location = productDeliveryLocation || [''];
            // Dim113 & 114
            tempDataLayer.product_is_future_dated = productShipDate;
            tempDataLayer.product_future_date = productIsShipInFuture;

            // subscription
            tempDataLayer.product_subscription_type = productSubscriptionType;
            tempDataLayer.product_subscription_interval = productSubscriptionInterval;
            tempDataLayer.product_subscription_duration = productSubscriptionDuration;
            tempDataLayer.affiliation = productBrandExpanded?.[0] || '';

            tempDataLayer.product_item_types = productItemType || [''];
            tempDataLayer.product_item_type_state = (productTypeState || ['']).map((type) => (type === 'REGULARPRODUCT' ? 'REGULAR_PRODUCT' : type));

            tempDataLayer.search_term_list = (searchTerm && searchTerm.length) ? searchTerm : productSearchTerm;

            tempDataLayer.total_recipients = productIds.length || 0;

            tempDataLayer.product_real_category_name = productListNames || productRealCategoryName || [''];
            // tempDataLayer.product_real_category_name_2 = productRealCategoryName2 || [''];
            // tempDataLayer.product_real_category_name_3 = productRealCategoryName3 || [''];
            // tempDataLayer.product_real_category_name_4 = productRealCategoryName4 || [''];
            // tempDataLayer.product_real_category_name_5 = productRealCategoryName5 || [''];
            tempDataLayer.banner_type = bannerType;
            tempDataLayer.sort = selectedSort;
            tempDataLayer.filters = selectedFilters;
            tempDataLayer.index = itemListPosition;
            tempDataLayer.search_term = searchTerm;
            tempDataLayer.banner_group = bannerGroup;
            tempDataLayer.banner_item_name = bannerItem;

            if (typeof window !== 'undefined' && window?.location?.href?.includes('/checkout/')) {
                tempDataLayer.order_product_total = Number(parseFloat(dataLayerProductHelpers.sumValues(totalProductPrice || [0]) + (dataLayerProductHelpers.sumValues(productItemShipping || [0]))).toFixed(2));
            }
            tempDataLayer.product_discount = productDiscount || [''];
        }
    } catch (ex) {
        mbpLogger.logError({
            products,
            impressions,
            props,
            function: 'generateDataLayerProductArrays',
            appName: process.env.npm_package_name,
            module: 'mbp-page',
            jsError: ex,
            message: 'Failed to generate product arrays.',
        });
    }

    tempDataLayer.products_currency = 'USD';
    return tempDataLayer;
};

export const convertPassportDataToProduct = (passport) => {
    const passportItem = passport?.item_id || null;
    if (passportItem) {
        const item = {};
        item[passportItem] = {
            brandId: passport?.brand_id || '',
            baseCode: passport?.item_id || '',
            partNumber: passport?.item_id,
            name: 'Passport',
            price: passport?.price,
            sKUs: [{ partNumber: passport?.item_id || '' }],
        };
        return item;
    }
    return {};
};

export const convertToProduct = (list) => {
    const products = {};
    if (list) {
        list.forEach((item) => {
            products[item.id] = item;
        });
    }
    return products;
};

export const convertMoviesDataToProduct = (movies) => {
    const movieProducts = {};
    (movies || []).forEach((movie) => {
        const movieSku = movie?.sku || null;
        movieProducts[movieSku] = {
            brandId: movie?.brand_id || '',
            baseCode: movie?.sku || '',
            partNumber: movie?.sku,
            name: `NBCU-${movie?.sku}`, // movie?.sku_description,
            price: movie?.sku_price,
            sKUs: [{ partNumber: movie?.sku || '' }],
        };
    });

    return movieProducts;
};
export default {
    generateDataLayerProductArrays,
    convertPassportDataToProduct,
    convertMoviesDataToProduct,
};
